import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import API_BASE_URL from '../config';
import ProductCard from '../components/products/ProductCard';
import Toast from '../components/common/Toast';
import { addToCart } from '../store/cartSlice';
import { resolveImageUrl } from '../utils/imageUrl';
import './ProductDetailPage.css';

// ─── Lens category / type helpers ────────────────────────────────────────────
// Primary lenses the customer picks as their BASE lens
const BASE_LENS_TYPES   = ['standard', 'high-index', 'progressive'];
// Add-on coatings that can be layered on top of the base lens
const ADDON_LENS_TYPES  = ['blue-cut', 'photochromic'];

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const prescriptionFileRef = useRef(null);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedLens, setSelectedLens] = useState(null);         // base lens
  const [selectedAddons, setSelectedAddons] = useState([]);       // array of add-ons (blue-cut, photochromic)
  const [selectedLensColor, setSelectedLensColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [allImages, setAllImages] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [lensOptions, setLensOptions] = useState([]);
  const [contactLensColors, setContactLensColors] = useState([]);

  // Hardcoded branded spectacle lens - FREE
  const BRANDED_SPECTACLE_LENS = {
    id: 'branded-spectacle-lens',
    name: 'Branded Spectacle Lens Only',
    price: 0,  // FREE
    lens_category: 'eyeglass',
    lens_type: 'branded',
    description: 'Premium branded spectacle lenses'
  };

  // ── Prescription ─────────────────────────────────────────────────────────
  const [prescriptionMode, setPrescriptionMode]         = useState('manual');
  const [prescription, setPrescription]                 = useState({ 
    right: { sph: '', cyl: '', axis: '', add: '', pd: '' }, 
    left: { sph: '', cyl: '', axis: '', add: '', pd: '' } 
  });
  const [prescriptionErrors, setPrescriptionErrors]     = useState({ 
    right: { sph: false, cyl: false, axis: false, add: false, pd: false }, 
    left: { sph: false, cyl: false, axis: false, add: false, pd: false } 
  });
  const [prescriptionFile, setPrescriptionFile]         = useState(null);
  const [prescriptionPreview, setPrescriptionPreview]   = useState(null);
  const [prescriptionUploading, setPrescriptionUploading] = useState(false);
  const [prescriptionImageUrl, setPrescriptionImageUrl] = useState(null);

  // ── Reviews ──────────────────────────────────────────────────────────────
  const [showReviewForm, setShowReviewForm]     = useState(false);
  const [reviewRating, setReviewRating]         = useState(5);
  const [reviewTitle, setReviewTitle]           = useState('');
  const [reviewComment, setReviewComment]       = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError]           = useState('');
  const [reviewSuccess, setReviewSuccess]       = useState('');
  const [reviews, setReviews]                   = useState([]);

  // Check if product is contact lens category
  const isContactLensCategory = product?.category_name?.toLowerCase().includes('contact') || 
                                 product?.category?.toLowerCase().includes('contact');

  // ─── Prescription option generators ───────────────────────────────────────
  const generateSphOptions = () => { 
    const o = ['0.00'];
    for (let i = 0.25; i <= 20; i += 0.25) o.push(`-${i.toFixed(2)}`); 
    for (let i = 0.25; i <= 20; i += 0.25) o.push(`+${i.toFixed(2)}`); 
    return o.sort((a, b) => parseFloat(a) - parseFloat(b)); 
  };
  
  const generateCylOptions = () => { 
    const o = ['0.00']; 
    for (let i = 0.25; i <= 5; i += 0.25) { 
      o.push(`-${i.toFixed(2)}`); 
      o.push(`+${i.toFixed(2)}`); 
    } 
    return o; 
  };
  
  const generateAxisOptions = () => Array.from({ length: 181 }, (_, i) => i.toString());
  
  const generateAddOptions = () => { 
    const o = []; 
    for (let i = 1.0; i <= 3.0; i += 0.25) o.push(`+${i.toFixed(2)}`); 
    return o; 
  };
  
  const generatePdOptions = () => { 
    const o = [];
    for (let i = 20; i <= 38; i += 0.5) {
      o.push(i.toFixed(1));
    }
    return o;
  };
  
  const sphOptions  = generateSphOptions();
  const cylOptions  = generateCylOptions();
  const axisOptions = generateAxisOptions();
  const addOptions  = generateAddOptions();
  const pdOptions   = generatePdOptions(); 

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });

  // ── Derived lens lists ────────────────────────────────────────────────────
  // Only include lenses that SUPPORT ADD-ONS (Standard, Bifocal, Progressive types)
  const LENS_TYPES_WITH_ADDONS = ['standard', 'bifocal', 'progressive'];
  
  const baseLenses = lensOptions.filter(l => 
    (l.lens_category === 'eyeglass' || l.lens_category === 'progressive') && 
    LENS_TYPES_WITH_ADDONS.includes(l.lens_type) &&
    !ADDON_LENS_TYPES.includes(l.lens_type)
  );
  
  const addonLenses     = lensOptions.filter(l => ADDON_LENS_TYPES.includes(l.lens_type));
  const contactLenses   = lensOptions.filter(l => l.lens_category === 'contact');
  
  // Check if selected lens supports add-ons
  const selectedLensSupportsAddons = selectedLens && LENS_TYPES_WITH_ADDONS.includes(selectedLens.lens_type);
  
  // Check if branded lens is selected
  const isBrandedLensSelected = selectedLens?.id === BRANDED_SPECTACLE_LENS.id;

  // Handle add-on toggle (allow multiple selections)
  const handleAddonToggle = (addon) => {
    setSelectedAddons(prev => {
      const exists = prev.find(a => a.id === addon.id);
      if (exists) {
        return prev.filter(a => a.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
  };

  // ── Fetch product ─────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/products/${id}`);
        if (response.ok) {
          const data = await response.json();
          setProduct(data.data);
          setLensOptions(data.data.lens_options || []);
          setContactLensColors(data.data.contact_lens_colors || []);
          setReviews(data.data.reviews || []);
          if (isAuthenticated && user) checkWishlistStatus();
          if (data.data.variants?.length > 0) setSelectedVariant(data.data.variants[0]);
          if (data.data.frame_sizes?.length > 0) setSelectedSize(data.data.frame_sizes[0]);
        } else { setError('Product not found'); }
      } catch { setError('Error loading product'); }
      finally { setLoading(false); }
    };
    fetchProduct();
  }, [id, isAuthenticated, user]);

  const checkWishlistStatus = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/wishlist/check/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (r.ok) { const d = await r.json(); setIsInWishlist(d.in_wishlist); }
    } catch {}
  };

  useEffect(() => {
    if (selectedVariant?.images?.length > 0) setAllImages(selectedVariant.images);
    else if (product?.images?.length > 0) setAllImages(product.images);
    else setAllImages([]);
    setSelectedImage(0);
  }, [selectedVariant, product]);

  // Reset add-ons and prescription when base lens changes
  useEffect(() => {
    setSelectedAddons([]);
    setPrescriptionMode('manual');
    setPrescription({ 
      right: { sph: '', cyl: '', axis: '', add: '', pd: '' }, 
      left: { sph: '', cyl: '', axis: '', add: '', pd: '' } 
    });
    setPrescriptionErrors({ 
      right: { sph: false, cyl: false, axis: false, add: false, pd: false }, 
      left: { sph: false, cyl: false, axis: false, add: false, pd: false } 
    });
    setPrescriptionFile(null); 
    setPrescriptionPreview(null); 
    setPrescriptionImageUrl(null);
  }, [selectedLens]);

  // ── Prescription file handlers ────────────────────────────────────────────
  const handlePrescriptionFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) { showToast('Allowed: JPG, PNG, WEBP, PDF', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('File must be under 5MB', 'error'); return; }
    setPrescriptionFile(file); 
    setPrescriptionImageUrl(null);
    if (file.type !== 'application/pdf') {
      const reader = new FileReader();
      reader.onloadend = () => setPrescriptionPreview(reader.result);
      reader.readAsDataURL(file);
    } else { setPrescriptionPreview(null); }
  };

  const handleUploadPrescription = async () => {
    if (!prescriptionFile) return;
    setPrescriptionUploading(true);
    try {
      const fd = new FormData(); 
      fd.append('prescription', prescriptionFile);
      const res = await fetch(`${API_BASE_URL}/upload-prescription`, { 
        method: 'POST', 
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, 
        body: fd 
      });
      const data = await res.json();
      if (res.ok && data.success) { 
        setPrescriptionImageUrl(data.data.image_url); 
        showToast('Prescription uploaded!', 'success'); 
      }
      else { showToast(data.message || 'Upload failed', 'error'); }
    } catch { showToast('Upload error', 'error'); }
    finally { setPrescriptionUploading(false); }
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validatePrescription = () => {
    if (!selectedLens) return true;
    if (prescriptionMode === 'upload') {
      if (!prescriptionImageUrl) { 
        showToast('Please upload your prescription image first', 'error'); 
        return false; 
      }
      return true;
    }
    const errors = { 
      right: { sph: false, cyl: false, axis: false, add: false, pd: false }, 
      left: { sph: false, cyl: false, axis: false, add: false, pd: false } 
    };
    let valid = true;
    
    // Right eye validation
    if (!prescription.right.sph) { errors.right.sph = true; valid = false; }
    if (prescription.right.cyl && !prescription.right.axis && prescription.right.cyl !== '0.00') { 
      errors.right.axis = true; valid = false; 
    }
    if (!prescription.right.pd) { errors.right.pd = true; valid = false; }
    
    // Left eye validation
    if (!prescription.left.sph) { errors.left.sph = true; valid = false; }
    if (prescription.left.cyl && !prescription.left.axis && prescription.left.cyl !== '0.00') { 
      errors.left.axis = true; valid = false; 
    }
    if (!prescription.left.pd) { errors.left.pd = true; valid = false; }
    
    setPrescriptionErrors(errors);
    return valid;
  };

  const handlePrescriptionChange = (eye, field, value) =>
    setPrescription(prev => ({ ...prev, [eye]: { ...prev[eye], [field]: value } }));

  // ── Price helpers ─────────────────────────────────────────────────────────
  const getCurrentPrice  = () => !product ? 0 : selectedVariant ? (selectedVariant.sale_price || selectedVariant.price) : (product.sale_price || product.price);
  const getOriginalPrice = () => !product ? 0 : selectedVariant ? selectedVariant.price : product.price;
  const getDiscountPercentage = () => { const c = getCurrentPrice(); const o = getOriginalPrice(); return o > c ? Math.round(((o - c) / o) * 100) : 0; };

  const getTotalPrice = () => {
    let total = parseFloat(getCurrentPrice()) || 0;
    if (selectedLens) {
      if (isBrandedLensSelected) {
        total += BRANDED_SPECTACLE_LENS.price;
      } else {
        total += parseFloat(selectedLens.price) || 0;
      }
    }
    if (selectedAddons.length > 0) {
      selectedAddons.forEach(addon => {
        total += parseFloat(addon.price) || 0;
      });
    }
    if (selectedLensColor && selectedLens?.lens_category === 'contact')
      total += parseFloat(selectedLensColor.price_adjustment) || 0;
    if (selectedSize?.price_adjustment) total += parseFloat(selectedSize.price_adjustment) || 0;
    return total;
  };

  const buildCartItem = () => {
    const imgUrl = resolveImageUrl(allImages[selectedImage]?.image_url || allImages[0]?.image_url);
    let prescriptionData = null;
    if (selectedLens) {
      prescriptionData = prescriptionMode === 'upload'
        ? { type: 'image', image_url: prescriptionImageUrl }
        : prescription;
    }
    
    // Get the correct lens info
    let lensId = selectedLens?.id || null;
    let lensName = selectedLens?.name || null;
    let lensPrice = selectedLens?.price || 0;
    
    if (isBrandedLensSelected) {
      lensId = BRANDED_SPECTACLE_LENS.id;
      lensName = BRANDED_SPECTACLE_LENS.name;
      lensPrice = BRANDED_SPECTACLE_LENS.price;
    }
    
    return {
      product_id:        product.id,
      product_name:      product.name,
      variant_id:        selectedVariant?.id    || null,
      variant_name:      selectedVariant?.variant_value || null,
      variant_color:     selectedVariant?.variant_color_code || null,
      size_id:           selectedSize?.id       || null,
      size_name:         selectedSize?.size_name || null,
      size_details:      selectedSize ? `${selectedSize.width}, Bridge: ${selectedSize.bridge}, Temple: ${selectedSize.temple}` : null,
      lens_id:           lensId,
      lens_name:         lensName,
      lens_price:        lensPrice,
      addon_lenses:      selectedAddons.map(addon => ({ id: addon.id, name: addon.name, price: addon.price })),
      lens_color_id:     selectedLensColor?.id  || null,
      lens_color_name:   selectedLensColor?.name || null,
      lens_color_code:   selectedLensColor?.color_code || null,
      prescription:      prescriptionData,
      quantity,
      price:             getTotalPrice(),
      image:             imgUrl
    };
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) { 
      navigate('/login', { state: { from: `/products/${id}`, message: 'Please login to add items to cart' } }); 
      return; 
    }
    if (selectedLens && !validatePrescription()) return;
    setActionLoading(true);
    try { 
      await dispatch(addToCart(buildCartItem())).unwrap(); 
      showToast('Product added to cart', 'success'); 
    }
    catch { showToast('Error adding to cart', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleBuyNow = async () => {
    if (!isAuthenticated) { 
      navigate('/login', { state: { from: `/products/${id}`, message: 'Please login to proceed' } }); 
      return; 
    }
    if (selectedLens && !validatePrescription()) return;
    setActionLoading(true);
    try { 
      await dispatch(addToCart(buildCartItem())).unwrap(); 
      navigate('/checkout'); 
    }
    catch { showToast('Error processing order', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) { 
      navigate('/login', { state: { from: `/products/${id}` } }); 
      return; 
    }
    try {
      const r = await fetch(`${API_BASE_URL}/wishlist/toggle`, { 
        method: 'POST', 
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${localStorage.getItem('token')}` 
        }, 
        body: JSON.stringify({ product_id: product.id, variant_id: selectedVariant?.id }) 
      });
      const d = await r.json();
      if (r.ok && d.success) { 
        setIsInWishlist(d.in_wishlist); 
        showToast(d.message, 'success'); 
      }
    } catch { showToast('Error updating wishlist', 'error'); }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!reviewComment.trim()) { setReviewError('Please write your review.'); return; }
    setReviewError(''); 
    setSubmittingReview(true);
    try {
      const r = await fetch(`${API_BASE_URL}/reviews/add`, { 
        method: 'POST', 
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${localStorage.getItem('token')}` 
        }, 
        body: JSON.stringify({ 
          product_id: product.id, 
          rating: reviewRating, 
          title: reviewTitle, 
          comment: reviewComment 
        }) 
      });
      const d = await r.json();
      if (r.ok && d.success) {
        setReviews(prev => [{ 
          id: d.review_id || Date.now(), 
          rating: reviewRating, 
          title: reviewTitle, 
          comment: reviewComment, 
          first_name: user?.first_name || 'You', 
          last_name: user?.last_name || '', 
          created_at: new Date().toISOString() 
        }, ...prev]);
        setReviewTitle(''); 
        setReviewComment(''); 
        setReviewRating(5); 
        setShowReviewForm(false);
        setReviewSuccess('Your review has been submitted and is pending approval.');
        showToast('Review submitted!', 'success');
      } else { setReviewError(d.message || 'Failed to submit review.'); }
    } catch { setReviewError('An error occurred.'); }
    finally { setSubmittingReview(false); }
  };

  const formatPrice = (price) => { 
    if (!price && price !== 0) return '₹0.00'; 
    const n = parseFloat(price); 
    return isNaN(n) ? '₹0.00' : `₹${n.toFixed(2)}`; 
  };
  
  const groupedVariants = () => { 
    if (!product?.variants) return {}; 
    return product.variants.reduce((acc, v) => { 
      if (!acc[v.variant_name]) acc[v.variant_name] = []; 
      acc[v.variant_name].push(v); 
      return acc; 
    }, {}); 
  };

  // ── Loading / Error ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="loading-skeleton">
            <div className="skeleton-grid">
              <div className="skeleton-gallery" />
              <div className="skeleton-info">
                <div className="skeleton-line large" />
                <div className="skeleton-line medium" />
                <div className="skeleton-line" />
                <div className="skeleton-line medium" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error || !product) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="error-message">
            <h3>{error || 'Product not found'}</h3>
            <Link to="/products">Back to Products</Link>
          </div>
        </div>
      </div>
    );
  }

  const variantsByType     = groupedVariants();
  const discountPercentage = getDiscountPercentage();
  const relatedProducts    = product.related_products || [];
  const frameSizes         = product.frame_sizes || [];
  const currentImageSrc    = resolveImageUrl(allImages[selectedImage]?.image_url);
  // Prescription needed for ANY lens selected (including branded spectacle lens)
  const needsPrescription  = selectedLens !== null;

  // Helper function to render a base lens with its add-on options
  const renderBaseLensWithAddons = (lens) => {
    const isSelected = selectedLens?.id === lens.id;
    const showAddons = isSelected && selectedLensSupportsAddons && addonLenses.length > 0;
    
    return (
      <div key={lens.id} className="base-lens-block">
        <label className={`lens-card ${isSelected ? 'selected' : ''}`}>
          <input 
            type="radio" 
            name="lens" 
            checked={isSelected}
            onChange={() => { 
              setSelectedLens(lens); 
              setSelectedAddons([]); 
              setSelectedLensColor(null); 
            }} 
          />
          <div className="lens-info">
            <div className="lens-name-row">
              <span className="lens-name">{lens.name}</span>
              {lens.lens_category === 'progressive' && lens.range_type && (
                <span className="lens-range-tag">{lens.range_type}</span>
              )}
            </div>
            <span className="lens-price">+₹{parseFloat(lens.price).toFixed(2)}</span>
            {lens.description && <span className="lens-desc">{lens.description}</span>}
          </div>
        </label>

        {/* Add-on coatings - ONLY shown for lenses that support them */}
        {showAddons && (
          <div className="addon-lenses-block">
            <p className="addon-lenses-label">
              <span className="addon-icon">✨</span>
              (Select any combination)
            </p>
            <div className="addon-lens-options">
              {addonLenses.map(addon => {
                const isAddonSelected = selectedAddons.some(a => a.id === addon.id);
                return (
                  <label key={addon.id} className={`addon-lens-card ${isAddonSelected ? 'selected' : ''}`}>
                    <input 
                      type="checkbox" 
                      checked={isAddonSelected}
                      onChange={() => handleAddonToggle(addon)} 
                    />
                    <div className="addon-lens-info">
                      <span className="addon-lens-name">
                        {addon.lens_type === 'blue-cut' && <span className="coating-badge coating-badge-blue">💙 Blue Cut</span>}
                        {addon.lens_type === 'photochromic' && <span className="coating-badge coating-badge-photo">🌈 Photochromic</span>}
                        {addon.name}
                      </span>
                      <span className="addon-lens-price">+₹{parseFloat(addon.price).toFixed(2)}</span>
                      {addon.description && <span className="addon-lens-desc">{addon.description}</span>}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render the Add Lenses section
  const renderAddLensesSection = () => (
    <div className="lens-section">
      <h4>Add Lenses <span className="optional-badge">Optional</span></h4>
      <div className="lens-options">

        {/* Frame Only */}
        <label className={`lens-card ${!selectedLens ? 'selected' : ''}`}>
          <input 
            type="radio" 
            name="lens" 
            checked={!selectedLens}
            onChange={() => { 
              setSelectedLens(null); 
              setSelectedAddons([]); 
              setSelectedLensColor(null); 
            }} 
          />
          <div className="lens-info">
            <span className="lens-name">Frame Only</span>
            <span className="lens-price">No lenses</span>
          </div>
        </label>

        {/* Branded Spectacle Lens Only - HARDCODED FREE */}
        <label className={`lens-card free-lens-card ${isBrandedLensSelected ? 'selected' : ''}`}>
          <input 
            type="radio" 
            name="lens" 
            checked={isBrandedLensSelected}
            onChange={() => { 
              setSelectedLens(BRANDED_SPECTACLE_LENS); 
              setSelectedAddons([]); 
              setSelectedLensColor(null); 
            }} 
          />
          <div className="lens-info">
            <span className="lens-name">{BRANDED_SPECTACLE_LENS.name}</span>
            <span className="lens-price free-price"></span>
            {BRANDED_SPECTACLE_LENS.description && (
              <span className="lens-desc">{BRANDED_SPECTACLE_LENS.description}</span>
            )}
          </div>
        </label>

        {/* Base lenses that SUPPORT add-ons */}
        {baseLenses.map(lens => renderBaseLensWithAddons(lens))}

      </div>
    </div>
  );

  return (
    <div className="product-detail-page">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Home</Link> / <Link to="/products">Products</Link> / <span>{product.name}</span>
        </div>

        <div className="product-detail-grid">
          {/* ── Gallery ── */}
          <div className="product-gallery">
            <div className="main-image">
              <img 
                src={currentImageSrc} 
                alt={product.name}
                onError={(e) => { 
                  e.target.src = 'https://via.placeholder.com/600x600?text=' + encodeURIComponent(product.name); 
                }} 
              />
              {discountPercentage > 0 && <span className="sale-badge">-{discountPercentage}%</span>}
              <button className={`wishlist-btn ${isInWishlist ? 'active' : ''}`} onClick={handleWishlistToggle}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill={isInWishlist ? '#e74c3c' : 'none'} stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
            </div>
            {allImages.length > 1 && (
              <div className="thumbnail-grid">
                {allImages.map((img, index) => (
                  <button 
                    key={index} 
                    className={`thumbnail ${selectedImage === index ? 'active' : ''}`} 
                    onClick={() => setSelectedImage(index)}
                  >
                    <img 
                      src={resolveImageUrl(img.image_url)} 
                      alt={`${product.name} ${index + 1}`}
                      onError={(e) => { 
                        e.target.src = 'https://via.placeholder.com/100x100?text=Image'; 
                      }} 
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Product Info ── */}
          <div className="product-info-detail">
            <h1 className="product-title">{product.name}</h1>
            <div className="product-meta">
              <span className="product-brand">{product.brand || 'Premium Brand'}</span>
              <div className="product-rating">
                <div className="stars">
                  {'★'.repeat(Math.floor(product.rating || 0))}
                  {'☆'.repeat(5 - Math.floor(product.rating || 0))}
                </div>
                <span className="rating-count">({reviews.length} reviews)</span>
              </div>
            </div>

            {/* Price — updates live as add-ons are selected */}
            <div className="product-price-detail">
              <span className="current-price">₹{getTotalPrice().toFixed(2)}</span>
              {getOriginalPrice() > getCurrentPrice() && (
                <>
                  <span className="original-price">₹{getOriginalPrice().toFixed(2)}</span>
                  <span className="discount-badge">Save {discountPercentage}%</span>
                </>
              )}
            </div>
            
            {/* Show price breakdown when add-ons are selected */}
            {(selectedLens || selectedAddons.length > 0) && (
              <div className="price-breakdown-inline">
                <span>{product.name}: ₹{parseFloat(getCurrentPrice()).toFixed(2)}</span>
                {selectedLens && (
                  <span>
                    + {isBrandedLensSelected ? BRANDED_SPECTACLE_LENS.name : selectedLens.name}: 
                    {isBrandedLensSelected 
                      ? ' FREE' 
                      : ` ₹${parseFloat(selectedLens.price).toFixed(2)}`
                    }
                  </span>
                )}
                {selectedAddons.map(addon => (
                  <span key={addon.id}>+ {addon.name}: ₹{parseFloat(addon.price).toFixed(2)}</span>
                ))}
              </div>
            )}
            
            <div className="product-availability">
              <span className="in-stock">✓ In Stock</span>
            </div>

            {/* Variants */}
            {Object.keys(variantsByType).map(variantName => (
              <div key={variantName} className="variant-section">
                <h4>{variantName}</h4>
                <div className="variant-options">
                  {variantsByType[variantName].map(variant =>
                    variant.variant_color_code ? (
                      <button 
                        key={variant.id} 
                        className={`color-option ${selectedVariant?.id === variant.id ? 'selected' : ''}`} 
                        style={{ backgroundColor: variant.variant_color_code }} 
                        onClick={() => setSelectedVariant(variant)} 
                        title={variant.variant_value} 
                      />
                    ) : (
                      <button 
                        key={variant.id} 
                        className={`variant-btn ${selectedVariant?.id === variant.id ? 'selected' : ''}`} 
                        onClick={() => setSelectedVariant(variant)}
                      >
                        {variant.variant_value}
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}

            {/* Frame Size */}
            {frameSizes.length > 0 && (
              <div className="size-section">
                <h4>Frame Size</h4>
                <div className="size-options">
                  {frameSizes.map(size => (
                    <button 
                      key={size.id} 
                      className={`size-btn ${selectedSize?.id === size.id ? 'selected' : ''}`} 
                      onClick={() => setSelectedSize(size)}
                    >
                      <span className="size-name">{size.size_name}</span>
                      <span className="size-dimensions">{size.width}</span>
                    </button>
                  ))}
                </div>
                {selectedSize && (
                  <div className="size-details">
                    <p>Bridge: {selectedSize.bridge} | Temple: {selectedSize.temple}</p>
                  </div>
                )}
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════
                CONDITIONAL SECTION ORDER:
                - Contact Lens Category → Contact Lenses FIRST, then Add Lenses SECOND
                - All Other Categories → Add Lenses FIRST, then Contact Lenses SECOND
               ═══════════════════════════════════════════════════════════ */}
            
            {isContactLensCategory ? (
              <>
                {/* Contact Lens Category: Contact Lenses FIRST */}
                {contactLenses.length > 0 && (
                  <div className="lens-section contact-lens-primary">
                    <h4>Contact Lenses <span className="required-badge">Select Option</span></h4>
                    <div className="lens-options">
                      {contactLenses.map(lens => (
                        <div key={lens.id}>
                          <label className={`lens-card contact-lens-card ${selectedLens?.id === lens.id ? 'selected' : ''}`}>
                            <input 
                              type="radio" 
                              name="lens" 
                              checked={selectedLens?.id === lens.id}
                              onChange={() => { 
                                setSelectedLens(lens); 
                                setSelectedAddons([]); 
                                setSelectedLensColor(null); 
                              }} 
                            />
                            <div className="lens-info">
                              <div className="lens-header">
                                <span className="lens-name">{lens.name}</span>
                                {lens.duration && <span className="lens-duration">{lens.duration}</span>}
                              </div>
                              <span className="lens-price">+₹{parseFloat(lens.price).toFixed(2)}</span>
                              {lens.description && <span className="lens-desc">{lens.description}</span>}
                            </div>
                          </label>
                          {/* Contact lens color selector */}
                          {selectedLens?.id === lens.id && contactLensColors.length > 0 && (
                            <div className="color-selection">
                              <h6>Select Color</h6>
                              <div className="color-options">
                                {contactLensColors.map(color => (
                                  <button 
                                    key={color.id} 
                                    className={`color-circle ${selectedLensColor?.id === color.id ? 'selected' : ''}`} 
                                    style={{ backgroundColor: color.color_code }} 
                                    onClick={() => setSelectedLensColor(color)} 
                                    title={color.name}
                                  >
                                    {selectedLensColor?.id === color.id && <span className="check-mark">✓</span>}
                                  </button>
                                ))}
                              </div>
                              {selectedLensColor && (
                                <p className="color-price">+₹{parseFloat(selectedLensColor.price_adjustment).toFixed(2)}</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Then Add Lenses SECOND */}
                {renderAddLensesSection()}
              </>
            ) : (
              <>
                {/* All Other Categories: Add Lenses FIRST */}
                {renderAddLensesSection()}

                {/* Then Contact Lenses SECOND */}
                {contactLenses.length > 0 && (
                  <div className="lens-section contact-lens-secondary">
                    <h4>Contact Lenses <span className="optional-badge">Optional</span></h4>
                    <div className="lens-options">
                      {contactLenses.map(lens => (
                        <div key={lens.id}>
                          <label className={`lens-card contact-lens-card ${selectedLens?.id === lens.id ? 'selected' : ''}`}>
                            <input 
                              type="radio" 
                              name="lens" 
                              checked={selectedLens?.id === lens.id}
                              onChange={() => { 
                                setSelectedLens(lens); 
                                setSelectedAddons([]); 
                                setSelectedLensColor(null); 
                              }} 
                            />
                            <div className="lens-info">
                              <div className="lens-header">
                                <span className="lens-name">{lens.name}</span>
                                {lens.duration && <span className="lens-duration">{lens.duration}</span>}
                              </div>
                              <span className="lens-price">+₹{parseFloat(lens.price).toFixed(2)}</span>
                              {lens.description && <span className="lens-desc">{lens.description}</span>}
                            </div>
                          </label>
                          {/* Contact lens color selector */}
                          {selectedLens?.id === lens.id && contactLensColors.length > 0 && (
                            <div className="color-selection">
                              <h6>Select Color</h6>
                              <div className="color-options">
                                {contactLensColors.map(color => (
                                  <button 
                                    key={color.id} 
                                    className={`color-circle ${selectedLensColor?.id === color.id ? 'selected' : ''}`} 
                                    style={{ backgroundColor: color.color_code }} 
                                    onClick={() => setSelectedLensColor(color)} 
                                    title={color.name}
                                  >
                                    {selectedLensColor?.id === color.id && <span className="check-mark">✓</span>}
                                  </button>
                                ))}
                              </div>
                              {selectedLensColor && (
                                <p className="color-price">+₹{parseFloat(selectedLensColor.price_adjustment).toFixed(2)}</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── Prescription Section ── */}
            {needsPrescription && (
              <div className="prescription-section">
                <h4>
                  Your Prescription
                  {selectedLens?.lens_category === 'contact' && (
                    <span className="required-badge">Required for Contact Lenses</span>
                  )}
                </h4>

                <div className="rx-mode-toggle">
                  <button 
                    type="button" 
                    className={`rx-mode-btn ${prescriptionMode === 'manual' ? 'active' : ''}`} 
                    onClick={() => setPrescriptionMode('manual')}
                  >
                    ✏️ Enter Manually
                  </button>
                  <button 
                    type="button" 
                    className={`rx-mode-btn ${prescriptionMode === 'upload' ? 'active' : ''}`} 
                    onClick={() => setPrescriptionMode('upload')}
                  >
                    📷 Upload Image
                  </button>
                </div>

                {prescriptionMode === 'manual' && (
                  <>
                    {/* Right Eye */}
                    <div className="prescription-eye">
                      <h5>Right Eye (OD)</h5>
                      <div className="prescription-grid">
                        <div className="prescription-field">
                          <label>SPH *</label>
                          <select 
                            value={prescription.right.sph}  
                            onChange={e => handlePrescriptionChange('right', 'sph', e.target.value)} 
                            className={prescriptionErrors.right.sph ? 'error' : ''}
                          >
                            <option value="">Select SPH</option>
                            {sphOptions.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        
                        <div className="prescription-field">
                          <label>CYL</label>
                          <select 
                            value={prescription.right.cyl}  
                            onChange={e => handlePrescriptionChange('right', 'cyl', e.target.value)}
                          >
                            <option value="">Select CYL</option>
                            {cylOptions.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        
                        <div className="prescription-field">
                          <label>AXIS</label>
                          <select 
                            value={prescription.right.axis} 
                            onChange={e => handlePrescriptionChange('right', 'axis', e.target.value)} 
                            disabled={!prescription.right.cyl || prescription.right.cyl === '0.00'} 
                            className={prescriptionErrors.right.axis ? 'error' : ''}
                          >
                            <option value="">Select AXIS</option>
                            {axisOptions.map(o => <option key={o} value={o}>{o}°</option>)}
                          </select>
                        </div>
                        
                        <div className="prescription-field">
                          <label>ADD</label>
                          <select 
                            value={prescription.right.add}  
                            onChange={e => handlePrescriptionChange('right', 'add', e.target.value)}
                          >
                            <option value="">Select ADD</option>
                            {addOptions.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        
                        <div className="prescription-field">
                          <label>PD (mm) *</label>
                          <select 
                            value={prescription.right.pd}   
                            onChange={e => handlePrescriptionChange('right', 'pd', e.target.value)} 
                            className={prescriptionErrors.right.pd ? 'error' : ''}
                          >
                            <option value="">Select PD</option>
                            {pdOptions.map(o => <option key={o} value={o}>{o} mm</option>)}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Left Eye */}
                    <div className="prescription-eye">
                      <h5>Left Eye (OS)</h5>
                      <div className="prescription-grid">
                        <div className="prescription-field">
                          <label>SPH *</label>
                          <select 
                            value={prescription.left.sph}  
                            onChange={e => handlePrescriptionChange('left', 'sph', e.target.value)} 
                            className={prescriptionErrors.left.sph ? 'error' : ''}
                          >
                            <option value="">Select SPH</option>
                            {sphOptions.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        
                        <div className="prescription-field">
                          <label>CYL</label>
                          <select 
                            value={prescription.left.cyl}  
                            onChange={e => handlePrescriptionChange('left', 'cyl', e.target.value)}
                          >
                            <option value="">Select CYL</option>
                            {cylOptions.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        
                        <div className="prescription-field">
                          <label>AXIS</label>
                          <select 
                            value={prescription.left.axis} 
                            onChange={e => handlePrescriptionChange('left', 'axis', e.target.value)} 
                            disabled={!prescription.left.cyl || prescription.left.cyl === '0.00'} 
                            className={prescriptionErrors.left.axis ? 'error' : ''}
                          >
                            <option value="">Select AXIS</option>
                            {axisOptions.map(o => <option key={o} value={o}>{o}°</option>)}
                          </select>
                        </div>
                        
                        <div className="prescription-field">
                          <label>ADD</label>
                          <select 
                            value={prescription.left.add}  
                            onChange={e => handlePrescriptionChange('left', 'add', e.target.value)}
                          >
                            <option value="">Select ADD</option>
                            {addOptions.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        
                        <div className="prescription-field">
                          <label>PD (mm) *</label>
                          <select 
                            value={prescription.left.pd}   
                            onChange={e => handlePrescriptionChange('left', 'pd', e.target.value)} 
                            className={prescriptionErrors.left.pd ? 'error' : ''}
                          >
                            <option value="">Select PD</option>
                            {pdOptions.map(o => <option key={o} value={o}>{o} mm</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {prescriptionMode === 'upload' && (
                  <div className="rx-upload-area">
                    <p className="rx-upload-hint">Upload a clear photo of your prescription — JPG, PNG, WEBP or PDF (max 5MB)</p>
                    <div 
                      className={`rx-drop-zone ${prescriptionFile ? 'has-file' : ''}`} 
                      onClick={() => prescriptionFileRef.current?.click()}
                    >
                      {prescriptionPreview ? (
                        <img src={prescriptionPreview} alt="Prescription preview" className="rx-preview-img" />
                      ) : prescriptionFile ? (
                        <div className="rx-file-selected">
                          <span>📄</span>
                          <p>{prescriptionFile.name}</p>
                        </div>
                      ) : (
                        <div className="rx-placeholder">
                          <span>📁</span>
                          <p>Click to select file</p>
                          <small>JPG, PNG, WEBP or PDF</small>
                        </div>
                      )}
                    </div>
                    <input 
                      ref={prescriptionFileRef} 
                      type="file" 
                      accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf" 
                      onChange={handlePrescriptionFileChange} 
                      style={{ display: 'none' }} 
                    />
                    {prescriptionFile && !prescriptionImageUrl && (
                      <button 
                        type="button" 
                        className="rx-upload-btn" 
                        onClick={handleUploadPrescription} 
                        disabled={prescriptionUploading}
                      >
                        {prescriptionUploading ? (
                          <><span className="spinner" /> Uploading...</>
                        ) : (
                          '⬆ Upload Prescription'
                        )}
                      </button>
                    )}
                    {prescriptionImageUrl && (
                      <div className="rx-upload-success">
                        <span>✅</span> Prescription uploaded successfully!
                        <button 
                          type="button" 
                          className="rx-reupload-btn" 
                          onClick={() => { 
                            setPrescriptionFile(null); 
                            setPrescriptionPreview(null); 
                            setPrescriptionImageUrl(null); 
                          }}
                        >
                          Change
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Quantity */}
            <div className="quantity-section">
              <h4>Quantity</h4>
              <div className="quantity-selector">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>-</button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} disabled={quantity >= 10}>+</button>
              </div>
            </div>

            <div className="action-buttons">
              <button className="add-to-cart-btn" onClick={handleAddToCart} disabled={actionLoading}>
                {actionLoading ? 'Adding...' : 'Add to Cart'}
              </button>
              <button className="buy-now-btn" onClick={handleBuyNow} disabled={actionLoading}>
                {actionLoading ? 'Processing...' : 'Buy Now'}
              </button>
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="product-tabs">
          <div className="tabs-header">
            <button 
              className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`} 
              onClick={() => setActiveTab('description')}
            >
              Description
            </button>
            <button 
              className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`} 
              onClick={() => setActiveTab('reviews')}
            >
              Reviews ({reviews.length})
            </button>
          </div>
          <div className="tab-content">
            {activeTab === 'description' && (
              <div className="description-tab">
                <p>{product.description}</p>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="reviews-tab">
                <div className="reviews-summary">
                  <div className="reviews-summary-left">
                    <div className="avg-rating-box">
                      <span className="avg-rating-number">
                        {product.rating ? parseFloat(product.rating).toFixed(1) : '0.0'}
                      </span>
                      <div className="avg-rating-stars">
                        {Array(5).fill(null).map((_, i) => (
                          <span 
                            key={i} 
                            className={`summary-star ${i < Math.round(product.rating || 0) ? 'filled' : ''}`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                      <span className="avg-rating-label">
                        {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
                      </span>
                    </div>
                  </div>
                  <div className="reviews-summary-right">
                    {isAuthenticated ? (
                      <button 
                        className="write-review-btn" 
                        onClick={() => { 
                          setShowReviewForm(!showReviewForm); 
                          setReviewError(''); 
                          setReviewSuccess(''); 
                        }}
                      >
                        {showReviewForm ? '✕ Cancel' : '✎ Write a Review'}
                      </button>
                    ) : (
                      <Link to="/login" className="login-to-review">Login to Write a Review</Link>
                    )}
                  </div>
                </div>
                
                {reviewSuccess && (
                  <div className="review-alert review-alert-success">
                    <span>✓</span> {reviewSuccess}
                  </div>
                )}
                
                {showReviewForm && isAuthenticated && (
                  <form onSubmit={handleReviewSubmit} className="review-form">
                    <h4>Share Your Experience</h4>
                    <div className="review-form-field">
                      <label>Your Rating <span className="required">*</span></label>
                      <div className="star-rating interactive">
                        {Array(5).fill(null).map((_, i) => (
                          <button 
                            type="button" 
                            key={i} 
                            className={`star-btn ${reviewRating > i ? 'active' : ''}`} 
                            onClick={() => setReviewRating(i + 1)}
                          >
                            ★
                          </button>
                        ))}
                        <span className="rating-label-text">
                          {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][reviewRating]}
                        </span>
                      </div>
                    </div>
                    <div className="review-form-field">
                      <label>Review Title</label>
                      <input 
                        type="text" 
                        placeholder="Summarize your experience (optional)" 
                        value={reviewTitle} 
                        onChange={e => setReviewTitle(e.target.value)} 
                        maxLength={255} 
                      />
                    </div>
                    <div className="review-form-field">
                      <label>Your Review <span className="required">*</span></label>
                      <textarea 
                        placeholder="Tell others what you think..." 
                        value={reviewComment} 
                        onChange={e => setReviewComment(e.target.value)} 
                        rows="5" 
                        required 
                      />
                    </div>
                    {reviewError && (
                      <div className="review-alert review-alert-error">
                        <span>✕</span> {reviewError}
                      </div>
                    )}
                    <div className="review-form-actions">
                      <button type="submit" className="submit-review-btn" disabled={submittingReview}>
                        {submittingReview ? <><span className="spinner" /> Submitting...</> : 'Submit Review'}
                      </button>
                      <button 
                        type="button" 
                        className="cancel-review-btn" 
                        onClick={() => { 
                          setShowReviewForm(false); 
                          setReviewError(''); 
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
                
                {reviews.length === 0 ? (
                  <div className="no-reviews">
                    <div className="no-reviews-icon">💬</div>
                    <p>No reviews yet.</p>
                  </div>
                ) : (
                  <div className="reviews-list">
                    {reviews.map(review => (
                      <div key={review.id} className="review-card">
                        <div className="review-card-header">
                          <div className="reviewer-avatar">
                            {(review.first_name || 'U')[0].toUpperCase()}
                          </div>
                          <div className="reviewer-info">
                            <span className="reviewer-name">
                              {review.first_name} {review.last_name}
                            </span>
                            <span className="review-date">
                              {new Date(review.created_at).toLocaleDateString('en-IN', { 
                                day: 'numeric', 
                                month: 'short', 
                                year: 'numeric' 
                              })}
                            </span>
                          </div>
                          <div className="review-stars">
                            {Array(5).fill(null).map((_, i) => (
                              <span 
                                key={i} 
                                className={`review-star ${i < review.rating ? 'filled' : ''}`}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                        </div>
                        {review.title && <h5 className="review-title">{review.title}</h5>}
                        <p className="review-comment">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <section className="related-products">
            <h2>You May Also Like</h2>
            <div className="related-grid">
              {relatedProducts.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </section>
        )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default ProductDetailPage;